/**
 * A draped landcover plate's **colours**, and the toggle that swaps them.
 *
 * `world-instancer.ts` is the region half — which refs there are, where each
 * copy goes, how the lot is folded into meshes and one line buffer. This is the
 * one question it kept asking that is not about placement at all: given a plate
 * that has been draped, what colour is each of its faces, and what changes when
 * ENHANCED TERRAIN is switched?
 *
 * Two treatments stack, and they stack in this order for a reason:
 *
 * 1. **Slope shading** (`terrain-drape.ts`'s `shadedPlateColours`) — a whole
 *    second colour array, built once at load, hillshading every face by the
 *    ground triangle it now lies inside. The toggle is a buffer swap.
 * 2. **G20's canopy ground paint** (`wood-ground-paint.ts`) — the ground under
 *    each derived wood, laid **over** the shaded buffer rather than baked into
 *    it. Sparse, because the painted share of a region's plates is under a
 *    percent of their faces, and separate because it keeps the two effects
 *    independently nameable and independently testable when they happen to
 *    ride one setting.
 *
 * That ordering is what makes the flip free in both directions: setting the
 * whole-plate buffer is what *removes* the paint, so neither buffer ever holds
 * it and the authentic path is the array it always was.
 */

import type * as THREE from "three/webgpu";
import { shadedPlateColours } from "./terrain-drape.ts";
import { applyWoodGroundPaint, woodGroundPaintFor, type FaceShader, type WoodGroundPaint } from "./wood-ground-paint.ts";
import type { TreeSite } from "./wood-siting.ts";

/** One draped plate that has a slope-shaded alternative, and possibly some wood ground under it. */
export interface ShadedPlate {
  readonly attribute: THREE.BufferAttribute;
  /** The authored tones, exactly as the plate loaded — what the toggle returns to. */
  readonly flat: Float32Array;
  /** Every face hillshaded by its own slope. */
  readonly shaded: Float32Array;
  /** G20's canopy ground paint, scattered over `shaded` when the toggle is on. */
  readonly canopy: WoodGroundPaint | undefined;
}

/**
 * The colour state for one already-draped plate, or `undefined` when there is
 * no shader to build it with.
 *
 * Only correct **after** `drapeGeometry`, and for the same reason in both
 * halves: cut on the ground mesh's own cells, a plate face lies inside one
 * ground triangle, so it has a single slope to shade by and a footprint small
 * enough for "is this face under a wood" to mean anything.
 */
export function shadedPlateFor(
  geometry: THREE.BufferGeometry,
  originX: number,
  originZ: number,
  landcoverShade: FaceShader | undefined,
  woodSites: readonly TreeSite[],
  rampShift: number,
): ShadedPlate | undefined {
  if (!landcoverShade) return undefined;
  const shaded = shadedPlateColours(geometry, landcoverShade);
  const attribute = geometry.getAttribute("color") as THREE.BufferAttribute | undefined;
  if (!shaded || !attribute) return undefined;
  return {
    attribute,
    flat: (attribute.array as Float32Array).slice(),
    shaded,
    canopy: woodGroundPaintFor(geometry, originX, originZ, woodSites, landcoverShade, rampShift),
  };
}

/**
 * Swaps every plate between its authored tone and the enhanced one.
 *
 * A buffer swap, not a rebuild: both arrays are built once at load, so a toggle
 * flip costs one upload per plate and no pipeline compile.
 */
export function applyLandcoverShading(plates: readonly ShadedPlate[], enabled: boolean): void {
  for (const plate of plates) {
    const array = plate.attribute.array as Float32Array;
    array.set(enabled ? plate.shaded : plate.flat);
    if (enabled && plate.canopy) applyWoodGroundPaint(array, plate.canopy);
    plate.attribute.needsUpdate = true;
  }
}

/**
 * What the canopy paint covers, reported rather than assumed.
 *
 * `woodsPainted` against `woodCount` is the census that matters: a wood whose
 * ground is not painted is a wood that still reads as speckles, and the
 * guarantee `wood-ground-paint.ts` argues for is that there are none of them.
 */
export interface WoodGroundPaintCensus {
  /** Wood sites offered to the paint. */
  readonly woodCount: number;
  /** How many of them painted at least one face somewhere in the region. */
  readonly woodsPainted: number;
  /** Plates carrying paint. */
  readonly plateCount: number;
  /** Faces repainted across the whole region — no triangle is added, these are the plate's own. */
  readonly faceCount: number;
}

/**
 * Folds the per-plate paints into one region-level census. A wood counts as
 * painted if *any* plate carries some of it, which is the honest union: several
 * landcover plates cover the same ground and a wood may straddle two.
 */
export function woodGroundPaintCensus(
  plates: readonly ShadedPlate[],
  woodSites: readonly TreeSite[],
): WoodGroundPaintCensus {
  const painted = new Uint8Array(woodSites.length);
  let plateCount = 0;
  let faceCount = 0;
  for (const plate of plates) {
    if (!plate.canopy) continue;
    plateCount += 1;
    faceCount += plate.canopy.faces.length;
    plate.canopy.sitesPainted.forEach((flag, index) => {
      if (flag) painted[index] = 1;
    });
  }
  return {
    woodCount: woodSites.length,
    woodsPainted: painted.reduce((total, flag) => total + flag, 0),
    plateCount,
    faceCount,
  };
}
